<template>
  <div v-click-outside="$_onCloseDropdown" ref="dropdownMenuButton" class="dropdown-menu-button">
    <LvText v-if="title" h6 bold>{{ $t(title) }}<LvText v-if="required" red>*</LvText></LvText>
    <ValidationProvider v-slot="{ errors }" :rules="rules" class="dropdown-wrap">
      <div class="dropdown-menu-button_content-wrap">
        <LvText v-if="beforeText" h5>
          {{ beforeText }}
        </LvText>
        <div
          class="dropdown-menu-button_content"
          :class="{
            outline: outline,
            widthFix180: widthFix180,
            widthFix200: widthFix200,
            disabled: disabled || !dropdownMenuItem || dropdownMenuItem.length === 0,
            error: errors.length > 0,
          }"
          @click="$_onClick"
        >
          <i v-if="!isLoading && leadingIcon" class="icon leading-icon" :class="leadingIcon" />
          <LoadingAnimate v-if="isLoading" primary class="dropdown-menu-button_content__loading" />
          <button v-else :class="{ select: value }">
            {{ _selectedItemText }}
          </button>
          <i
            v-if="!disableCancel && value"
            class="dropdown-menu-button_content_cancel-icon icon-cancel mr-30"
            @click.stop="$_onCancel"
          />
          <i
            v-if="outline"
            class="icon trailing-icon outline icon-chevron-up"
            :class="{ show: isMenuShow }"
          />
          <i v-if="disabled" class="icon trailing-icon icon-chevron-down" />
          <i
            v-else-if="!outline && !isMenuShow"
            class="icon trailing-icon icon-arrow-drop-down-circle"
          />
          <i
            v-else-if="!outline && isMenuShow"
            class="icon trailing-icon icon-arrow-drop-up-circle"
          />
        </div>
        <transition mode="out-in">
          <div
            v-if="isMenuShow"
            class="dropdown-menu"
            :class="{
              'drop-up': scroll !== undefined ? isDropUp : false,
              'z-index-221': zIndex221,
            }"
          >
            <div v-if="hasSearch" class="search-tool">
              <SearchInput :value="searchValue" placeholder="搜尋" @input="$_onSearch($event)" />
            </div>
            <perfect-scrollbar class="ps dropdown-scrollable-content">
              <div
                v-for="(item, item_k) in _dropdownMenuItems"
                :key="item_k"
                class="dropdown-menu-item"
                :class="{ selected: $_isSelected(item) }"
                @click="$_onSelect(item)"
              >
                {{ $t(item.textLabel) }}
              </div>
            </perfect-scrollbar>
          </div>
        </transition>
        <LvText v-if="afterText" class="dropdown-menu-button_after-text">
          {{ afterText }}
        </LvText>
        <input type="hidden" :value="value" />
      </div>
      <div v-if="errors && errors.length > 0" class="invalid-text">
        <i class="icon-warning invalid-icon" />
        <LvText
          v-for="(text_e, text_k) in errors"
          :key="text_k"
          red
          remark
          class="invalid-text_text"
        >
          {{ $t(text_e) }}
        </LvText>
      </div>
      <div v-if="remindText && ((errors && errors.length <= 0) || !errors)" class="invalid-text">
        <i class="icon-warning invalid-icon" />
        <LvText red remark class="invalid-text_text">{{ $t(remindText) }}</LvText>
      </div>
    </ValidationProvider>
  </div>
</template>

<script>
import ClickOutside from 'vue-click-outside';
import { ValidationProvider } from 'vee-validate';
import { extend } from 'vee-validate';
import { required } from 'vee-validate/dist/rules';
import H_common from '@/helpers/common';

extend('required', {
  ...required,
  message: '此項目為必填',
});
export default {
  name: 'DropdownMenuButton',
  directives: {
    ClickOutside,
  },
  components: {
    ValidationProvider,
  },
  props: {
    dropdownMenuItem: {
      type: Array,
      default() {
        return [];
      },
    },
    leadingIcon: {
      type: String,
      default: '',
    },
    outline: {
      type: Boolean,
      default: false,
    },
    widthFix180: {
      type: Boolean,
      default: false,
    },
    widthFix200: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    title: {
      type: String,
      default: '',
    },
    required: {
      type: Boolean,
      default: false,
    },
    placeholder: {
      type: String,
      default: '',
    },
    value: {
      type: [String, Number, Array],
      default: null,
    },
    remindText: {
      type: String,
      default: '',
    },
    beforeText: {
      type: String,
      default: '',
    },
    afterText: {
      type: String,
      default: '',
    },
    rules: {
      type: String,
      default: '',
    },
    defaultValue: {
      type: [Number, String, Array],
      default: null,
    },
    hasSearch: {
      type: Boolean,
      default: false,
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
    scroll: {
      type: Number,
    },
    zIndex221: {
      type: Boolean,
      default: false,
    },
    disableCancel: {
      type: Boolean,
      default: false,
    },
    dropUpLowerRange: {
      type: Number,
      default: 350,
    },
    dropUpUpperRange: {
      type: Number,
      default: 350,
    },
  },
  data() {
    return {
      isMenuShow: false,
      templateContent: null,
      searchValue: '',
      dropdownHeight: 0,
      dropdownTop: 0,
      isDropUp: false,
    };
  },
  computed: {
    _selectedItemText() {
      if (this.dropdownMenuItem.length === 0) return this.$t('無選項');
      if (!this.value) return this._placeholderText;
      /// 點檢題目編輯時的風險會是number type，但，dropdownMenuItem的id是string type，所以需要用 == 來比較
      const selectedItem = this.dropdownMenuItem.find(item => item.id == this.value);
      return selectedItem ? this.$t(selectedItem.textLabel) : this._placeholderText;
    },
    _dropdownMenuItems() {
      if (!this.hasSearch) return this.dropdownMenuItem;
      const searchValue = this.searchValue.toLowerCase();
      return this.dropdownMenuItem.filter(item =>
        item.textLabel.toLowerCase().includes(searchValue),
      );
    },
    _placeholderText() {
      return this.placeholder ? this.$t(this.placeholder) : this.$t('選擇');
    },
  },
  watch: {
    scroll() {
      this.$_getDropdownHeight();
      this.$_isDropUp();
    },
  },
  created() {
    window.addEventListener('resize', this.$_getDropdownHeight);
    window.addEventListener('resize', this.$_isDropUp);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.$_getDropdownHeight);
    window.removeEventListener('resize', this.$_isDropUp);
  },
  mounted() {
    if (this.value === null && this.defaultValue !== null) {
      this.$emit('input', this.defaultValue);
    } else {
      this.$emit('input', this.value);
    }
  },
  methods: {
    $_onClick() {
      if (
        this.disabled ||
        this.isLoading ||
        !this.dropdownMenuItem ||
        this.dropdownMenuItem.length === 0
      )
        return;
      this.$_toggleDropdown();
    },
    $_toggleDropdown() {
      this.isMenuShow = !this.isMenuShow;
    },
    $_onSelect(item) {
      this.isMenuShow = false;
      this.$emit('input', item.id);
      this.$emit('click', item.id);
      this.$emit('clicked-data', item);
    },
    $_isSelected(item) {
      return item.id === this.value;
    },
    $_onCloseDropdown() {
      this.isMenuShow = false;
    },
    $_onSearch($event) {
      this.searchValue = $event;
    },
    $_getDropdownHeight() {
      const { height, y } = H_common.getComponentHeightAndY(this.$refs.dropdownMenuButton);
      this.dropdownHeight = height;
      this.dropdownTop = y;
    },
    $_isDropUp() {
      this.isDropUp = H_common.isDropUp(
        this.dropdownHeight,
        this.dropdownTop,
        this.dropUpUpperRange,
        this.dropUpLowerRange,
      );
    },
    $_onCancel() {
      this.$emit('input', null);
      this.$emit('click', null);
      this.$emit('clicked-data', null);
    },
  },
};
</script>
<style lang="scss">
@import '@/assets/scss/component/dropdown-menu-button';
</style>
